export type Plan = "starter" | "pro";

export type PlatformKey = "braise" | "square" | "toast" | "clover";

export type PlatformTotals = {
  label: string;
  total: number;
  inPerson: number;
  online: number;
  platform: number;
};

export type CostCalculatorInput = {
  averageOrderValue: number;
  ordersPerMonth: number;
  inPersonSharePercent: number;
  salesTaxRate: number;
  tipRate: number;
  marketingAutomation: boolean;
  marketingSmsCredits: number;
  plan: Plan;
  squareSoftwareFee: number;
  toastSoftwareFee: number;
  cloverSoftwareFee: number;
};

export type CostCalculatorResult = {
  totals: Record<PlatformKey, PlatformTotals>;
  gmv: number;
  base: number;
  orders: number;
  inPersonOrders: number;
  onlineOrders: number;
  inPersonShare: number;
  onlineShare: number;
  savingsVsSquare: number;
  savingsVsSquareYear: number;
  planNudgeDelta: number;
  averageOrderValue: number;
};

const PLAN_STARTER_PERCENT = 0.005;
const PLAN_STARTER_MIN = 19;
const PLAN_PRO_FLAT = 49;
const MARKETING_AUTOMATION_BASE = 29;
const MARKETING_AUTOMATION_INCLUDED_SMS = 500;
const MARKETING_AUTOMATION_SMS_RATE = 0.015;

const BRAISE_IN_PERSON_PERCENT = 0.027;
const BRAISE_IN_PERSON_FIXED = 0.05;
const SQUARE_IN_PERSON_PERCENT = 0.026;
const SQUARE_IN_PERSON_FIXED = 0.15;
const TOAST_IN_PERSON_PERCENT = 0.0249;
const TOAST_IN_PERSON_FIXED = 0.15;
const CLOVER_IN_PERSON_PERCENT = 0.023;
const CLOVER_IN_PERSON_FIXED = 0.1;
const UNIVERSAL_ONLINE_PERCENT = 0.029;
const UNIVERSAL_ONLINE_FIXED = 0.3;

export const clampPercentage = (value: number): number => {
  if (Number.isNaN(value)) {
    return 0;
  }
  return Math.min(100, Math.max(0, value));
};

export const calculateCostComparison = (
  input: CostCalculatorInput
): CostCalculatorResult => {
  const averageOrderValue = Number.isFinite(input.averageOrderValue)
    ? input.averageOrderValue
    : 0;
  const orders = Number.isFinite(input.ordersPerMonth)
    ? Math.trunc(input.ordersPerMonth)
    : 0;
  const inPersonSharePercent = Number.isFinite(input.inPersonSharePercent)
    ? input.inPersonSharePercent
    : 0;
  const normalizedInPerson = clampPercentage(inPersonSharePercent) / 100;
  const normalizedOnline = Math.max(0, 1 - normalizedInPerson);
  const salesTaxRate = Number.isFinite(input.salesTaxRate) ? input.salesTaxRate : 0;
  const tipRate = Number.isFinite(input.tipRate) ? input.tipRate : 0;
  const marketingSmsCredits = Number.isFinite(input.marketingSmsCredits)
    ? Math.max(0, Math.trunc(input.marketingSmsCredits))
    : 0;
  const squareSoftwareFee = Number.isFinite(input.squareSoftwareFee)
    ? Math.max(0, input.squareSoftwareFee)
    : 0;
  const toastSoftwareFee = Number.isFinite(input.toastSoftwareFee)
    ? Math.max(0, input.toastSoftwareFee)
    : 0;
  const cloverSoftwareFee = Number.isFinite(input.cloverSoftwareFee)
    ? Math.max(0, input.cloverSoftwareFee)
    : 0;

  const gmv = averageOrderValue * orders;
  const base = gmv === 0 ? 0 : (gmv * (1 + salesTaxRate)) / (1 + salesTaxRate + tipRate);
  const inPersonOrders = orders * normalizedInPerson;
  const onlineOrders = Math.max(0, orders - inPersonOrders);

  const braiseInPerson = inPersonOrders * (BRAISE_IN_PERSON_PERCENT * averageOrderValue + BRAISE_IN_PERSON_FIXED);
  const squareInPerson = inPersonOrders * (SQUARE_IN_PERSON_PERCENT * averageOrderValue + SQUARE_IN_PERSON_FIXED);
  const toastInPerson = inPersonOrders * (TOAST_IN_PERSON_PERCENT * averageOrderValue + TOAST_IN_PERSON_FIXED);
  const cloverInPerson = inPersonOrders * (CLOVER_IN_PERSON_PERCENT * averageOrderValue + CLOVER_IN_PERSON_FIXED);
  const onlineProcessing = onlineOrders * (UNIVERSAL_ONLINE_PERCENT * averageOrderValue + UNIVERSAL_ONLINE_FIXED);

  const braisePlanFee =
    input.plan === "starter"
      ? Math.max(PLAN_STARTER_PERCENT * base, PLAN_STARTER_MIN)
      : PLAN_PRO_FLAT;
  const marketingFee = input.marketingAutomation
    ? MARKETING_AUTOMATION_BASE +
      Math.max(0, marketingSmsCredits - MARKETING_AUTOMATION_INCLUDED_SMS) * MARKETING_AUTOMATION_SMS_RATE
    : 0;

  const totals: Record<PlatformKey, PlatformTotals> = {
    braise: {
      label: "Braise",
      total: braiseInPerson + onlineProcessing + braisePlanFee + marketingFee,
      inPerson: braiseInPerson,
      online: onlineProcessing,
      platform: braisePlanFee + marketingFee,
    },
    square: {
      label: "Square",
      total: squareInPerson + onlineProcessing + squareSoftwareFee,
      inPerson: squareInPerson,
      online: onlineProcessing,
      platform: squareSoftwareFee,
    },
    toast: {
      label: "Toast",
      total: toastInPerson + onlineProcessing + toastSoftwareFee,
      inPerson: toastInPerson,
      online: onlineProcessing,
      platform: toastSoftwareFee,
    },
    clover: {
      label: "Clover",
      total: cloverInPerson + onlineProcessing + cloverSoftwareFee,
      inPerson: cloverInPerson,
      online: onlineProcessing,
      platform: cloverSoftwareFee,
    },
  };

  const savingsVsSquare = totals.square.total - totals.braise.total;
  const savingsVsSquareYear = savingsVsSquare * 12;
  const planNudgeDelta =
    input.plan === "starter" && braisePlanFee > PLAN_PRO_FLAT ? braisePlanFee - PLAN_PRO_FLAT : 0;

  return {
    totals,
    gmv,
    base,
    orders,
    inPersonOrders,
    onlineOrders,
    inPersonShare: normalizedInPerson,
    onlineShare: normalizedOnline,
    savingsVsSquare,
    savingsVsSquareYear,
    planNudgeDelta,
    averageOrderValue,
  };
};
